if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.viewer = this.querySelector('.product-media-modal__viewer');
        this.slides = this.querySelectorAll('.product-media-modal__slide');
        this.thumbnailItems = this.querySelectorAll('.product-media-modal__thumbnail-item');
        this.thumbnailButtons = this.querySelectorAll('.product-media-modal__thumbnail');
        this.thumbnailList = this.querySelector('.product-media-modal__thumbnail-list');
        this.sliderComponent = this.querySelector('.product-media-modal__gallery');
        this.content = this.querySelector('.product-media-modal__content');
        this.gallery = this.querySelector('.product-media-modal__gallery');
        this.viewerWrap = this.querySelector('.product-media-modal__viewer-wrap');
        this.thumbnailsWrap = this.querySelector('.product-media-modal__thumbnails-wrap');
        this.closeButton = this.querySelector('.product-media-modal__toggle');
        this.prevButton = this.querySelector('.product-media-modal__viewer-wrap .slider-button--prev');
        this.nextButton = this.querySelector('.product-media-modal__viewer-wrap .slider-button--next');
        this.sliderButtons = this.querySelector('.product-media-modal__viewer-wrap .slider-buttons');
        this.currentVariantMediaId = null;

        [this.viewerWrap, this.thumbnailsWrap].forEach((safeZone) => {
          if (!safeZone) return;
          ['click', 'pointerup', 'mousedown', 'mouseup', 'touchend'].forEach((eventName) => {
            safeZone.addEventListener(eventName, (event) => event.stopPropagation());
          });
        });

        if (this.closeButton) {
          ['click', 'pointerup', 'mousedown', 'mouseup', 'touchend'].forEach((eventName) => {
            this.closeButton.addEventListener(eventName, (event) => event.stopPropagation());
          });
        }

        ['click', 'pointerup', 'touchend'].forEach((eventName) => {
          this.addEventListener(eventName, (event) => {
            if (
              event.target.closest(
                '.product-media-modal__viewer-wrap, .product-media-modal__thumbnails-wrap, .product-media-modal__toggle'
              )
            ) return;
            this.hide();
          });
        });

        this.thumbnailItems.forEach((thumbnailItem) => {
          const button = thumbnailItem.querySelector('button');
          if (!button) return;
          ['click', 'pointerup', 'mousedown', 'mouseup', 'touchend'].forEach((eventName) => {
            button.addEventListener(eventName, (event) => event.stopPropagation());
          });
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.setActiveMediaById(thumbnailItem.dataset.target);
          });
        });

        if (this.sliderComponent) {
          this.sliderComponent.addEventListener('slideChanged', (event) => {
            const currentElement = event.detail.currentElement;
            if (!currentElement) return;
            this.syncActiveState(currentElement.dataset.mediaId);
            this.playSlideMedia(currentElement);
            window.requestAnimationFrame(() => {
              this.syncViewerButtons(currentElement.dataset.mediaId);
            });
          });
        }

        this.viewer?.addEventListener('scroll', debounce(() => this.syncViewerButtons(), 120));

        this.syncViewerButtons();
      }

      hide() {
        const activeSlide = this.querySelector('.product-media-modal__slide.active');
        super.hide();
        document.body.classList.remove('product-media-modal-open');
        if (activeSlide) {
          const mediaId = activeSlide.dataset.mediaId;
          const sectionId = this.id.replace('ProductModal-', '');
          const gallery = document.querySelector(`#MediaGallery-${sectionId}`);
          if (gallery && gallery.setActiveMedia) {
            gallery.setActiveMedia(`${sectionId}-${mediaId}`, false);
          }
        }
      }

      show(opener) {
        super.show(opener);
        document.body.classList.add('product-media-modal-open');
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.setActiveMediaById(this.openedBy.getAttribute('data-media-id'));
      }

      setActiveMediaById(mediaId) {
        if (!mediaId || !this.viewer) return;

        const activeSlide = this.querySelector(`.product-media-modal__slide[data-media-id="${mediaId}"]`);
        if (!activeSlide) return;

        this.syncActiveState(mediaId);

        window.requestAnimationFrame(() => {
          if (this.sliderComponent && this.sliderComponent.resetPages) this.sliderComponent.resetPages();
          this.viewer.scrollTo({
            left: activeSlide.offsetLeft,
            behavior: 'smooth',
          });
          this.syncViewerButtons(mediaId);
        });

        this.playSlideMedia(activeSlide);
      }

      playSlideMedia(slide) {
        if (!slide) return;

        const deferredMedia = slide.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);

        window.requestAnimationFrame(() => {
          const video = deferredMedia.querySelector('video');
          if (video) {
            video.play().catch(() => {});
            return;
          }

          const youtube = deferredMedia.querySelector('.js-youtube');
          if (youtube && youtube.contentWindow) {
            youtube.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            return;
          }

          const vimeo = deferredMedia.querySelector('.js-vimeo');
          if (vimeo && vimeo.contentWindow) {
            vimeo.contentWindow.postMessage('{"method":"play"}', '*');
          }
        });
      }

      syncActiveState(mediaId) {
        const activeSlide = this.querySelector(`.product-media-modal__slide[data-media-id="${mediaId}"]`);
        if (activeSlide?.classList.contains('product-media-modal__slide--variant')) {
          this.currentVariantMediaId = mediaId;
        }

        this.slides.forEach((slide) => {
          const isActive = slide.dataset.mediaId === mediaId;
          const isOtherVariant =
            this.currentVariantMediaId &&
            slide.classList.contains('product-media-modal__slide--variant') &&
            slide.dataset.mediaId !== this.currentVariantMediaId;

          slide.classList.toggle('active', isActive);
          slide.classList.toggle('is-hidden-variant', Boolean(isOtherVariant));
        });

        this.thumbnailItems.forEach((thumbnailItem) => {
          const isActive = thumbnailItem.dataset.target === mediaId;
          const isOtherVariant =
            this.currentVariantMediaId &&
            thumbnailItem.classList.contains('product-media-modal__thumbnail-item--variant') &&
            thumbnailItem.dataset.target !== this.currentVariantMediaId;

          thumbnailItem.classList.toggle('active', isActive);
          thumbnailItem.classList.toggle('is-hidden-variant', Boolean(isOtherVariant));
        });

        this.thumbnailButtons.forEach((button) => button.removeAttribute('aria-current'));
        const activeThumbnail = this.querySelector(`.product-media-modal__thumbnail-item[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          const activeButton = activeThumbnail.querySelector('button');
          if (activeButton) activeButton.setAttribute('aria-current', 'true');
          this.scrollThumbnailIntoView(activeThumbnail);
        }
      }

      syncViewerButtons(mediaId) {
        if (!this.sliderButtons || !this.prevButton || !this.nextButton) return;
        const thumbnailItems = Array.from(this.querySelectorAll('.product-media-modal__thumbnail-item')).filter(
          (item) => item.offsetParent !== null
        );

        if (thumbnailItems.length <= 1) {
          this.sliderButtons.style.display = 'none';
          return;
        }

        this.sliderButtons.style.display = '';
        const activeThumbnailButton = this.querySelector('.product-media-modal__thumbnail[aria-current="true"]');
        const activeThumbnailItem = activeThumbnailButton?.closest('.product-media-modal__thumbnail-item');
        const currentMediaId =
          mediaId || activeThumbnailItem?.dataset.target || this.querySelector('.product-media-modal__slide.active')?.dataset.mediaId;
        const currentIndex = thumbnailItems.findIndex((item) => item.dataset.target === currentMediaId);

        if (currentIndex <= 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
          this.prevButton.setAttribute('hidden', 'hidden');
          this.prevButton.style.display = 'none';
        } else {
          this.prevButton.removeAttribute('disabled');
          this.prevButton.removeAttribute('hidden');
          this.prevButton.style.display = '';
        }

        if (currentIndex === -1 || currentIndex >= thumbnailItems.length - 1) {
          this.nextButton.setAttribute('disabled', 'disabled');
          this.nextButton.setAttribute('hidden', 'hidden');
          this.nextButton.style.display = 'none';
        } else {
          this.nextButton.removeAttribute('disabled');
          this.nextButton.removeAttribute('hidden');
          this.nextButton.style.display = '';
        }
      }

      scrollThumbnailIntoView(thumbnailItem) {
        if (!this.thumbnailList || !thumbnailItem) return;

        const targetLeft =
          thumbnailItem.offsetLeft - this.thumbnailList.clientWidth / 2 + thumbnailItem.clientWidth / 2;

        this.thumbnailList.scrollTo({
          left: Math.max(targetLeft, 0),
          behavior: 'smooth',
        });
      }
    }
  );
}
